import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/hooks/useLanguage";
import { CurrencyProvider } from "@/hooks/useCurrency";
import { OnboardingPausedProvider, PausedBanner } from "@/components/OnboardingPaused";
import Maintenance from "./pages/Maintenance";
const CursorEffects = lazy(() => import("@/components/CursorEffects"));
import Index from "./pages/Index";

// =====================================================================
// 🚧 MAINTENANCE MODE 🚧
// While `true`, the site renders only the Maintenance page.
// No routes mount, no Supabase/AI/Razorpay/realtime calls are made,
// no providers initialise. Set to `false` to bring the site back online.
// =====================================================================
const MAINTENANCE_MODE = false;

// Lazy-loaded routes for code splitting
const Product = lazy(() => import("./pages/Product"));
const Pricing = lazy(() => import("./pages/Pricing"));
const FAQ = lazy(() => import("./pages/FAQ"));
const RiskDisclaimerPage = lazy(() => import("./pages/legal/RiskDisclaimer"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Refund = lazy(() => import("./pages/legal/Refund"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Resources = lazy(() => import("./pages/Resources"));
const NivexaDemoAccess = lazy(() => import("./pages/NivexaDemoAccess"));
const CrmDashboard = lazy(() => import("./pages/CrmDashboard"));
const EmailPitcher = lazy(() => import("./pages/EmailPitcher"));



const queryClient = new QueryClient();

const App = () => {
  if (MAINTENANCE_MODE) {
    return (
      <HelmetProvider>
        <TooltipProvider>
          <Maintenance />
        </TooltipProvider>
      </HelmetProvider>
    );
  }

  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <CurrencyProvider>
        <TooltipProvider>
        <OnboardingPausedProvider>
          <CursorEffects />
          <PausedBanner />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/product" element={<Product />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/legal/risk-disclaimer" element={<RiskDisclaimerPage />} />
                <Route path="/legal/terms" element={<Terms />} />
                <Route path="/legal/privacy" element={<Privacy />} />
                <Route path="/legal/refund" element={<Refund />} />
                <Route path="/legal/cookies" element={<CookiePolicy />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/nivexa-demo-access" element={<NivexaDemoAccess />} />
                <Route path="/crm-dashboard" element={<CrmDashboard />} />
                <Route path="/admin/email-pitcher" element={<EmailPitcher />} />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </OnboardingPausedProvider>
        </TooltipProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
